import React, { useRef, useState } from 'react';

import { HistoryIcon, PauseIcon, PlayIcon } from './icons.js';
import { headOf, hm, hms, seekTo, useNow } from './media.js';

/* The recording the timeline shows: the last hour, the window a desk
   officer scrubs through most. */
export const WINDOW = 60 * 60 * 1000;

/* Released this close to the live edge, the playhead snaps back to live. */
const SNAP = 4000;

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/**
 * The scrubber under the selected camera. The right edge is now; drag
 * left and the feed seeks back through the recording with the readout
 * saying where ("Playback 13:42:05"); Go live returns. Events on this
 * camera are marked along it and seek to themselves when tapped, and a
 * stretch the camera was down for shows as a gap.
 *
 * `playback` is null when live, or the { at, setAt, paused } that
 * media.js describes; `onChange` receives the next one.
 */
export function Timeline({ playback, onChange, events = [], gaps = [], label }) {
    const now = useNow();
    const track = useRef(null);
    const [dragAt, setDragAt] = useState(null);
    const lastSent = useRef(0);

    const start = now - WINDOW;
    const head = headOf(playback, now);
    const shown = dragAt ?? head;
    const live = !playback && dragAt === null;
    const pos = t => clamp((t - start) / WINDOW, 0, 1) * 100;

    const fromPointer = event => {
        const rect = track.current.getBoundingClientRect();
        const f = clamp((event.clientX - rect.left) / Math.max(rect.width, 1), 0, 1);
        return start + f * WINDOW;
    };

    const commit = at => {
        if (now - at < SNAP) onChange(null);
        else onChange(seekTo(at));
    };

    const onPointerDown = event => {
        if (event.button !== undefined && event.button !== 0) return;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        const at = fromPointer(event);
        setDragAt(at);
        commit(at);
    };

    /* The feed seeks as the playhead moves, a few times a second rather
       than on every pointer event. */
    const onPointerMove = event => {
        if (dragAt === null) return;
        const at = fromPointer(event);
        setDragAt(at);
        const t = Date.now();
        if (t - lastSent.current > 120) {
            lastSent.current = t;
            commit(at);
        }
    };

    const onPointerUp = event => {
        if (dragAt === null) return;
        commit(fromPointer(event));
        setDragAt(null);
    };

    const skip = seconds => {
        const at = clamp(head + seconds * 1000, start, now);
        commit(at);
    };

    const togglePause = () => {
        if (playback && playback.paused) onChange({ at: playback.at, setAt: Date.now(), paused: false });
        else onChange({ at: head, setAt: Date.now(), paused: true });
    };

    const onKeyDown = event => {
        const step = event.shiftKey ? 60 : 10;
        if (event.key === 'ArrowLeft') { event.preventDefault(); skip(-step); }
        else if (event.key === 'ArrowRight') { event.preventDefault(); skip(step); }
        else if (event.key === 'End') { event.preventDefault(); onChange(null); }
        else if (event.key === 'Home') { event.preventDefault(); commit(start); }
    };

    /* A tick every ten minutes, labelled. */
    const ticks = [];
    for (let t = Math.ceil(start / 600000) * 600000; t <= now; t += 600000) ticks.push(t);

    const readout = live ? 'Live' : `${playback && playback.paused && dragAt === null ? 'Paused' : 'Playback'} ${hms(shown)}`;

    return (
        <div className={`cam-timeline${live ? ' cam-timeline--live' : ' cam-timeline--playback'}`}>
            <div className="cam-timeline__bar">
                <span className="cam-timeline__readout" aria-live="polite">
                    <span className="cam-timeline__dot" aria-hidden="true" />
                    {readout}
                </span>
                <div className="cam-timeline__controls">
                    <button type="button" className="cam-ibtn cam-ibtn--sm" onClick={() => skip(-30)} aria-label="Back 30 seconds" title="Back 30 seconds">
                        <HistoryIcon aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="cam-ibtn cam-ibtn--sm"
                        onClick={togglePause}
                        aria-label={playback && playback.paused ? 'Play' : 'Pause'}
                        title={playback && playback.paused ? 'Play' : 'Pause'}
                    >
                        {playback && playback.paused ? <PlayIcon aria-hidden="true" /> : <PauseIcon aria-hidden="true" />}
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${live ? 'btn-outline-light' : 'btn-primary'} cam-timeline__live`}
                        onClick={() => onChange(null)}
                        disabled={live}
                    >
                        Go live
                    </button>
                </div>
            </div>

            <div
                ref={track}
                className={`cam-timeline__track${dragAt !== null ? ' cam-timeline__track--drag' : ''}`}
                role="slider"
                tabIndex={0}
                aria-label={label ? `Recording, ${label}` : 'Recording'}
                aria-valuemin={0}
                aria-valuemax={60}
                aria-valuenow={Math.round((now - shown) / 60000)}
                aria-valuetext={live ? 'Live' : `${hms(shown)}, ${Math.round((now - shown) / 60000)} minutes ago`}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={() => setDragAt(null)}
                onKeyDown={onKeyDown}
            >
                <span className="cam-timeline__rec" aria-hidden="true" />
                {gaps.map(gap => {
                    const from = pos(gap.from);
                    const to = pos(gap.to ?? now);
                    if (to <= 0 || to - from <= 0) return null;
                    return (
                        <span
                            key={gap.from}
                            className="cam-timeline__gap"
                            style={{ left: `${from}%`, width: `${to - from}%` }}
                            aria-hidden="true"
                        />
                    );
                })}
                {!live && (
                    <span
                        className="cam-timeline__ahead"
                        style={{ left: `${pos(shown)}%` }}
                        aria-hidden="true"
                    />
                )}
                {ticks.map(t => (
                    <span
                        key={t}
                        className={`cam-timeline__tick${new Date(t).getMinutes() % 20 ? ' cam-timeline__tick--minor' : ''}`}
                        style={{ left: `${pos(t)}%` }}
                        aria-hidden="true"
                    >
                        {pos(t) < 93 && <span>{hm(t)}</span>}
                    </span>
                ))}
                {events.filter(e => e.at > start).map(e => (
                    <button
                        type="button"
                        key={e.id}
                        className={`cam-timeline__event cam-timeline__event--${e.kind}`}
                        style={{ left: `${pos(e.at)}%` }}
                        title={`${e.title}, ${hms(e.at)}`}
                        aria-label={`${e.title} at ${hms(e.at)}`}
                        onPointerDown={ev => ev.stopPropagation()}
                        onClick={() => commit(e.at - 5000)}
                    />
                ))}
                <span className="cam-timeline__head" style={{ left: `${pos(shown)}%` }} aria-hidden="true">
                    <span className="cam-timeline__knob" />
                </span>
            </div>
        </div>
    );
}
